import "../sentry";
import * as Sentry from "@sentry/react";
import React, {useCallback, useEffect, useState} from 'react';
import {createRoot} from 'react-dom/client';
import {BrowserRouter as Router} from 'react-router-dom';
import ThemeProvider from './mosaic/utils/ThemeContext';
import DatixoApplication from './DatixoApplication.jsx';
import GlobalContextProvider from "./utils/context/GlobalContextProvider.jsx";
import {Toaster} from "react-hot-toast";
import toast from "react-hot-toast";
import {GoogleOAuthProvider} from "@react-oauth/google";
import DashboardFilterContextProvider from "./components/filters/DashboardFilterContextProvider.jsx";
import {DataGrid2Provider} from "./components/datagrid/DataGrid2Context.jsx";
import ApplicationFailure from "./pages/error/ApplicationFailure.jsx";

function SessionFlashMessages() {
    useEffect(() => {
        const flashErrorMessage = sessionStorage.getItem('_flashErrorMessage');

        if (!flashErrorMessage) {
            return;
        }

        sessionStorage.removeItem('_flashErrorMessage');
        toast.error(flashErrorMessage);
    }, []);

    return null;
}

const simulateApplicationFailure = import.meta.env.DEV
    && new URLSearchParams(window.location.search).get('simulate_application_failure') === '1';

function ApplicationRuntime() {
    const [applicationFailed, setApplicationFailed] = useState(simulateApplicationFailure);
    const stopApplication = useCallback(() => {
        if (import.meta.env.DEV) {
            const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            window.location.replace(`/application-failure.html?startup=1&return_to=${encodeURIComponent(returnTo)}`);
            return;
        }
        setApplicationFailed(true);
    }, []);

    useEffect(() => {
        if (applicationFailed && import.meta.env.DEV) stopApplication();
    }, [applicationFailed, stopApplication]);

    if (applicationFailed) {
        return <ApplicationFailure startup/>;
    }

    return <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
        <React.StrictMode>
            <Toaster position="bottom-right" toastOptions={{duration: 4000}}/>
            <SessionFlashMessages/>
            <DataGrid2Provider>
                <Router>
                    <ThemeProvider>
                        <GlobalContextProvider>
                            <DashboardFilterContextProvider>
                                <DatixoApplication onFatalError={stopApplication}/>
                            </DashboardFilterContextProvider>
                        </GlobalContextProvider>
                    </ThemeProvider>
                </Router>
            </DataGrid2Provider>
        </React.StrictMode>
    </GoogleOAuthProvider>;
}

createRoot(document.getElementById('root')).render(
    <Sentry.ErrorBoundary fallback={() => import.meta.env.DEV ? <DevelopmentFailureRedirect/> : <ApplicationFailure/>}>
        <ApplicationRuntime/>
    </Sentry.ErrorBoundary>
);

function DevelopmentFailureRedirect() {
    useEffect(() => {
        const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.replace(`/application-failure.html?return_to=${encodeURIComponent(returnTo)}`);
    }, []);
    return <ApplicationFailure/>;
}
